import { useState, useEffect } from 'react';
import { ReviewQueueSubTab, isDepositOpenForLinking } from '@dill/dill-shared';
import { OneClickReviewQueue } from './OneClickReviewQueue';
import { ExceptionsQueue } from './ExceptionsQueue';
import { AwaitingSettlementQueue } from './AwaitingSettlementQueue';
import { UnmatchedDepositsQueue } from './UnmatchedDepositsQueue';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import {
  fetchRemittanceRecordsThunk,
  fetchOpenInvoicesThunk,
  fetchBankTransactionsThunk,
} from '../../../../redux/services/cashApplicationService';

export const CashApplicationReviewQueue = () => {
  const dispatch = useAppDispatch();
  const [activeSubTab, setActiveSubTab] = useState<ReviewQueueSubTab>('oneClick');
  const { oneClickReviewItems, exceptionItems, awaitingSettlementItems, bankTransactions } =
    useAppSelector((state) => state.cashApplication);
  const user = useAppSelector((state) => state.auth.user);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  useEffect(() => {
    const supplierId = currentSupplier?.id;

    if (supplierId) {
      dispatch(fetchRemittanceRecordsThunk({ supplierId }));
      dispatch(fetchOpenInvoicesThunk({ supplierId }));
      dispatch(fetchBankTransactionsThunk({ supplierId }));
    }
  }, [dispatch, currentSupplier?.id]);

  const oneClickCount = oneClickReviewItems.length;
  const exceptionsCount = exceptionItems.length;
  // Includes partially matched deposits: a bulk deposit still owed some of its checks is
  // outstanding work, and the count is what tells the operator there is any.
  const unmatchedDepositsCount = bankTransactions.filter(isDepositOpenForLinking).length;

  const tabs = [
    { id: 'oneClick' as ReviewQueueSubTab, label: '1-Click Review', count: oneClickCount },
    { id: 'exceptions' as ReviewQueueSubTab, label: 'Exceptions', count: exceptionsCount },
    {
      id: 'awaitingSettlement' as ReviewQueueSubTab,
      label: 'Awaiting Bank',
      count: awaitingSettlementItems.length,
    },
    {
      id: 'unmatchedDeposits' as ReviewQueueSubTab,
      label: 'Unmatched Deposits',
      count: unmatchedDepositsCount,
    },
  ];

  return (
    <div className="flex flex-col w-full h-full">
      {/* Tab Navigation */}
      <div className="sticky top-0 bg-white z-30 border-b border-gray-200">
        <div className="px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeSubTab === tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent ${
                activeSubTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              {tab.label}
              <span
                className={
                  'ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-xs font-semibold ' +
                  (activeSubTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600')
                }>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col w-full h-full min-h-0 px-4 pb-4 pt-2">
        {activeSubTab === 'oneClick' && <OneClickReviewQueue />}
        {activeSubTab === 'exceptions' && <ExceptionsQueue />}
        {activeSubTab === 'awaitingSettlement' && <AwaitingSettlementQueue />}
        {activeSubTab === 'unmatchedDeposits' && <UnmatchedDepositsQueue />}
      </div>
    </div>
  );
};
